import React, { useEffect, useMemo, useState } from "react";
import useStyles from "../../../components/common/model/styles";
import Popup from "../../../components/common/model/Popup";
import closeIcon from "../../../assets/closeModel.png";
import checkedIcon from "../../../assets/check-mark.png";
import deleteIcon from "../../../assets/trash.png";
import { DialogContent, DialogContentText, Grid } from "@mui/material";
import Notification from "../../../components/common/notification/Notification";
import Validation from "../../../utils/Validation";
import FormField from "../../../components/common/FormField";
import { useDispatch } from "react-redux";
import { setNotificationAction } from "../../../store/reducers/mmymSlice";
import { formElementFields } from "./costsAllFields";
import ResetFormAlert from "../settings/manageReffer/ResetFormAlert";
import { useSelector } from "react-redux";
import {
  getAllComplaintsAction,
  getComplaintByIdAction,
} from "../../../store/reducers/complaintSlice";
import { getAllSubUserAction } from "../../../store/reducers/subUserSlice";
import dayjs from "dayjs";

interface CostDetail {
  [key: string]: any;
  complaint_id: any | null;
  progress_id: any | null;
  description: any | null;
  added_by: any | null;
  date: any | null;
  amount: any | null;
  invoice_reference: any | null;
  supplier: any | null;
}

interface ManageCostsProps {
  open: boolean;
  onHide: () => void;
  title: string;
  selectCost: any;
  isSmallScreen?: boolean;
  onSave?: (data: any) => void;
}

const initialCostState: CostDetail = {
  complaint_id: null,
  progress_id: null,
  description: null,
  added_by: null,
  date: null,
  amount: null,
  invoice_reference: null,
  supplier: null,
};

const ManageCosts = (props: ManageCostsProps) => {
  const classes = useStyles();
  const { selectCost, onHide, open, title, onSave } = props;
  const dispatch = useDispatch();
  const [costDetail, setCostDetail] = useState<CostDetail>(initialCostState);
  const { users } = useSelector((state: any) => state.subUser);
  const { userProfile } = useSelector((state: any) => state.user);
  const { singleComplaint, complaints } = useSelector(
    (state: any) => state?.complaint,
  );
  const activeComplaintId = costDetail?.complaint_id || selectCost?.complaint_id;

  const mapCostToState = (cost: any) => ({
    complaint_id: cost?.complaint_id ? String(cost.complaint_id) : null,
    progress_id: cost?.progress_id ? String(cost.progress_id) : null,
    description: cost?.description || null,
    added_by: cost?.added_by || null,
    date: cost?.date || null,
    amount:
      cost?.amount === 0 || cost?.amount === "0"
        ? "0"
        : cost?.amount
          ? String(cost.amount)
          : null,
    invoice_reference: cost?.invoice_reference || null,
    supplier: cost?.supplier || null,
  });

  const getValidationFieldId = (fieldId: string) =>
    fieldId === "description" ? "requiredDescription" : fieldId;

  useEffect(() => {
    if (activeComplaintId && open) {
      dispatch(getComplaintByIdAction({ complaint_id: activeComplaintId }));
    }
  }, [dispatch, activeComplaintId, open]);

  useEffect(() => {
    if (!open) return;

    dispatch(getAllSubUserAction({ status: "ACTIVE" }));
    dispatch(
      getAllComplaintsAction({
        page: 1,
        limit: 200,
        orderBy: "",
        orderIn: "asc",
        status: "Initiated",
      }),
    );
  }, [dispatch, open]);

  const fieldsToRender = useMemo(() => {
    const addedByOptions = (users || []).map((user: any) => ({
      label: user.name,
      id: user.staff_id ?? user?.tenant_id,
    }));

    const complaintOptions = (complaints || []).map((complaint: any) => ({
      label: complaint.title,
      id: String(complaint.complaint_id),
    }));

    const selectedComplaintId =
      costDetail?.complaint_id ||
      (selectCost?.complaint_id ? String(selectCost.complaint_id) : "");
    const selectedComplaintLabel =
      selectCost?.complaint_name ||
      singleComplaint?.title ||
      singleComplaint?.complaint_name ||
      "";

    if (
      selectedComplaintId &&
      !complaintOptions.some(
        (option: any) => String(option.id) === String(selectedComplaintId),
      )
    ) {
      complaintOptions.unshift({
        id: String(selectedComplaintId),
        label:
          selectedComplaintLabel || `Complaint #${String(selectedComplaintId)}`,
      });
    }

    const progressOptions = (singleComplaint?.progress || []).map(
      (progress: any) => ({
        label: progress.description,
        id: String(progress.progress_id),
      }),
    );

    return formElementFields.map((field) => {
      if (field.id === "added_by") {
        return { ...field, options: addedByOptions };
      }
      if (field.id === "complaint_id") {
        return { ...field, options: complaintOptions };
      }
      if (field.id === "progress_id") {
        return { ...field, options: progressOptions };
      }
      return field;
    });
  }, [users, complaints, singleComplaint, costDetail?.complaint_id, selectCost]);

  useEffect(() => {
    if (singleComplaint && userProfile && !selectCost?.cost_id) {
      setCostDetail((prevState) => ({
        ...prevState,
        complaint_id: singleComplaint.complaint_id,
        added_by: userProfile?.staff_id ?? userProfile?.tenant_id, // Set default added_by to current user
        date: dayjs().format("YYYY-MM-DD") || null,
      }));
    }
  }, [singleComplaint, userProfile, selectCost?.cost_id]);

  const [alertModelShow, setAlertModelShow] = useState(false);
  const [isClickReset, setIsClickReset] = useState(false);

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
    isErrorOptional,
  ]: any = Validation({
    added_by: costDetail?.added_by,
    amount: costDetail?.amount,
    requiredDescription: costDetail?.description,
    date: costDetail?.date,
  });

  const [isActionCalled, setIsActionCalled] = useState(false);

  useEffect(() => {
    if (selectCost) {
      setCostDetail(mapCostToState(selectCost));
    }
  }, [selectCost]);

  const hideHandler = () => {
    onHide();
    handleClear();
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  };

  const handleClear = () => {
    if (selectCost?.cost_id) {
      setCostDetail(mapCostToState(selectCost));
    } else {
      setCostDetail({
        ...initialCostState,
        complaint_id: activeComplaintId || null,
        added_by: userProfile?.staff_id ?? userProfile?.tenant_id,
        date: dayjs().format("YYYY-MM-DD"),
      });
    }
    resetAllValidator();
    setIsClickReset(false);
    setAlertModelShow(false);
  };

  const openClearPopup = () => {
    setAlertModelShow(true);
  };

  useEffect(() => {
    if (isClickReset) {
      handleClear();
      setAlertModelShow(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClickReset]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {

      if (
        !isError() &&
        !isErrorOptional() &&
        error !== null &&
        isActionCalled
      ) {
        const modifiedCostDetail = {
          ...costDetail,
          amount: Number(costDetail.amount) || 0, // ✅ convert to number
        };

        if (onSave) {
          onSave(modifiedCostDetail);
        }
      }

      setIsActionCalled(false);
    }
  }, [
    isActionCalled,
    costDetail,
    error,
    isError,
    isErrorOptional,
    onSave,
  ]);

  const handleInputChange = (field: string, value: any) => {
    const normalizedValue =
      field === "complaint_id" || field === "progress_id"
        ? value
          ? String(value)
          : ""
        : value;

    setCostDetail((prevState) => ({
      ...prevState,
      [field]: normalizedValue,
    }));
    resetValidator(field);
  };

  const onBlurAction = (e: any) => {
    checkValidator(getValidationFieldId(e.target.id));
  };

  return (
    <>
      <Popup
        open={open}
        title={title}
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"md"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#fff"
        header={
          <>
            <img
              onClick={(e: any) => handleSubmit(e)}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />
            <img
              onClick={openClearPopup}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="delete"
            />
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "16px 10px !important" }}>
          <DialogContentText>
            <>
              <Notification />
              <Grid
                container
                direction="row"
                spacing={0.5}
                rowGap={0}
                rowSpacing={0}
                sx={{
                  background: "#E5F4F1",
                  padding: "10px !important",
                  marginTop: "10px !important",
                  borderRadius: "5px",
                  border: "1px solid #CFCFCF",
                  overflow: "hidden",
                }}
              >
                {fieldsToRender.map((field, i) => {
                  return (
                    <FormField
                      key={field.id}
                      onPaste={(e) =>
                        field?.id === "added_by" ? "" : e.preventDefault()
                      }
                      onKeyDown={(event: any) => {
                        console.log(
                          "event log-",
                          event,
                          event.target.value + event.key,
                        );
                        if (
                          event.keyCode === 8 ||
                          event.keyCode === 9 ||
                          event.keyCode === 37 ||
                          event.keyCode === 38 ||
                          event.keyCode === 39 ||
                          event.keyCode === 40 ||
                          event.keyCode === 18 ||
                          event.keyCode === 17 ||
                          event.ctrlKey
                        )
                          return event;
                        if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(event.key)
                        ) {
                          event.preventDefault();
                        } else if (
                          field?.patternAllowed &&
                          !field?.patternAllowed.test(
                            event.target.value + event.key,
                          )
                        ) {
                          event.preventDefault();
                        }
                      }}
                      {...{
                        ...field,
                        ...{
                          variant: "outlined",
                          inSingleGridForm: true,
                          isNonMarginTop: true,
                          InputProps: {
                            ...(field?.inputProps && field?.inputProps),
                            sx: {
                              height:
                                field?.id === "details" &&
                                costDetail[field?.id]?.length > 36
                                  ? "auto"
                                  : field?.type === "textarea"
                                    ? "auto"
                                    : "36px",
                              padding:
                                field?.id === "details" ||
                                field?.type === "textarea"
                                  ? "4px"
                                  : "",
                            },
                            autocomplete: "cost-new-field",
                          },
                          value: costDetail ? costDetail[field.id] : "",
                          onBlur: onBlurAction,
                          onChange: (e: any) =>
                            handleInputChange(field.id, e.target.value),
                          error:
                            error && error[getValidationFieldId(field?.id)],
                        },
                      }}
                    />
                  );
                })}
              </Grid>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alertModelShow && (
        <ResetFormAlert
          open={alertModelShow}
          title=""
          alertModalShow={alertModelShow}
          setAlertModelShow={setAlertModelShow}
          setIsClickReset={setIsClickReset}
        />
      )}
    </>
  );
};

export default ManageCosts;
